import { api } from '@/lib/api';
import type {
  IApiResponse,
  IDevExNormalizedListFilters,
  IDevExNormalizedListResult,
  IDevExRawListFilters,
  IDevExRawListResult,
  IDevExTeamMappingRule,
  IDevExTeamMappingRuleUpsert,
  IDevExTeamUnmatchedRow,
  IDevExUnmatchedListFilters,
  IDevExUnmatchedListResult,
  IDevExUnmatchedMetric,
  IDevExUnmatchedMetricFilters,
} from '@skystone/shared';

function appendStringParam(params: URLSearchParams, key: string, value: string | undefined): void {
  if (value) {
    params.set(key, value);
  }
}

function appendNumberParam(params: URLSearchParams, key: string, value: number | undefined): void {
  if (typeof value === 'number') {
    params.set(key, String(value));
  }
}

function appendLimitParam(params: URLSearchParams, value: number | undefined): void {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 1) {
    params.set('limit', String(value));
  }
}

export const devExApi = {
  listRaw: async (filters?: IDevExRawListFilters): Promise<IDevExRawListResult> => {
    const params = new URLSearchParams();
    appendStringParam(params, 'quarter', filters?.quarter);
    appendStringParam(params, 'sourceTable', filters?.sourceTable);
    appendStringParam(params, 'teamMappingStatus', filters?.teamMappingStatus);
    appendLimitParam(params, filters?.limit);
    appendNumberParam(params, 'offset', filters?.offset);

    const query = params.toString();
    const url = query ? `/devex/quarterly/raw?${query}` : '/devex/quarterly/raw';
    const result = await api.get<IApiResponse<IDevExRawListResult>>(url);
    return result.data;
  },

  listNormalized: async (filters?: IDevExNormalizedListFilters): Promise<IDevExNormalizedListResult> => {
    const params = new URLSearchParams();
    appendStringParam(params, 'quarter', filters?.quarter);
    appendStringParam(params, 'sourceTable', filters?.sourceTable);
    appendStringParam(params, 'teamMappingStatus', filters?.teamMappingStatus);
    appendStringParam(params, 'teamMappingReason', filters?.teamMappingReason);
    appendStringParam(params, 'mappedTeamId', filters?.mappedTeamId);
    appendStringParam(params, 'mappedProductId', filters?.mappedProductId);
    appendLimitParam(params, filters?.limit);
    appendNumberParam(params, 'offset', filters?.offset);

    const query = params.toString();
    const url = query ? `/devex/quarterly/normalized?${query}` : '/devex/quarterly/normalized';
    const result = await api.get<IApiResponse<IDevExNormalizedListResult>>(url);
    return result.data;
  },

  listUnmatched: async (filters?: IDevExUnmatchedListFilters): Promise<IDevExUnmatchedListResult> => {
    const params = new URLSearchParams();
    appendStringParam(params, 'quarter', filters?.quarter);
    appendStringParam(params, 'sourceTable', filters?.sourceTable);
    appendStringParam(params, 'reason', filters?.reason);
    appendStringParam(params, 'status', filters?.status);
    appendLimitParam(params, filters?.limit);
    appendNumberParam(params, 'offset', filters?.offset);

    const query = params.toString();
    const url = query ? `/devex/quarterly/unmatched?${query}` : '/devex/quarterly/unmatched';
    const result = await api.get<IApiResponse<IDevExUnmatchedListResult>>(url);
    return result.data;
  },

  listMappingRules: async (targetTeamId?: string): Promise<IDevExTeamMappingRule[]> => {
    const params = new URLSearchParams();
    if (targetTeamId) params.set('targetTeamId', targetTeamId);
    const query = params.toString();
    const url = query ? `/devex/team-mappings/rules?${query}` : '/devex/team-mappings/rules';
    const result = await api.get<IApiResponse<IDevExTeamMappingRule[]>>(url);
    return result.data;
  },

  listMappingUnmatched: async (): Promise<IDevExTeamUnmatchedRow[]> => {
    const result = await api.get<IApiResponse<IDevExTeamUnmatchedRow[]>>('/devex/team-mappings/unmatched?status=open');
    return result.data;
  },

  upsertMappingRule: async (body: IDevExTeamMappingRuleUpsert): Promise<IDevExTeamMappingRule> => {
    const result = await api.put<IApiResponse<IDevExTeamMappingRule>>('/devex/team-mappings/rules', body);
    return result.data;
  },

  deleteMappingRule: async (id: string): Promise<void> => {
    await api.delete<IApiResponse<{ deleted: boolean }>>(`/devex/team-mappings/rules/${id}`);
  },

  getUnmatchedMetrics: async (filters?: IDevExUnmatchedMetricFilters): Promise<IDevExUnmatchedMetric[]> => {
    const params = new URLSearchParams();
    appendStringParam(params, 'quarter', filters?.quarter);
    appendStringParam(params, 'sourceTable', filters?.sourceTable);

    const query = params.toString();
    const url = query ? `/devex/quarterly/unmatched/metrics?${query}` : '/devex/quarterly/unmatched/metrics';
    const result = await api.get<IApiResponse<IDevExUnmatchedMetric[]>>(url);
    return result.data;
  },
};
